import React, { FC, useEffect, memo, useState, useCallback, useMemo } from "react";
import { UpOutlined, DownOutlined } from "src/icons";
/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { abbreviateNumber, safeGet } from "src/shared/utils";
import { AccordianNode, NestedTreeModel } from "src/shared/models/DetailsModel";
import { IReduxOperations } from "src/store";
import Shimmer from "src/shared/components/CustomisedShimmer";
import SearchInputBox from "./SearchInputBox";
import { ReactComponent as FilterIcon } from "src/assets/filter-icon-horizontal.svg";
import { notification } from "antd";
import { setAlert, setNotification } from "src/shared/utils/alert";

import {
  Holder,
  AccordianSection,
  FirstLevelAccordianItem,
  FirstLevelExpandedBottom,
  FirstLevelHeader,
  FirstHeaderItemName,
  SecondLevelAccordianItem,
  SecondLevelHeader,
  HeaderItemName,
  SecondLevelExpandedBottom,
  ThirdLevelAccordianItem,
  ButtonSection,
  LoadButton,
  shimmerConfig,
  NoDataText,
} from "./styles";
import { NotificationType } from "src/shared/types";

const PAGINATION_NUMBER = 4;

enum DIMENSIONS {
  FIRST_DIMENSION,
  SECOND_DIMENSION,
  THIRD_DIMENSION,
}

export enum DATA_UNIT {
  PERCENTAGE,
  DEFAULT,
}

export type AccordianChartProps = {
  chartDataProps: IReduxOperations<NestedTreeModel>;
  selectedDimensions: Map<keyof typeof DIMENSIONS, string>;
  dataUnit: DATA_UNIT;
  isMetricDataLoading: boolean;
  onApplyFilter: (values: any) => void;
};

/** Styles */
const shimmerCSS = css`
  margin-top: 20px;
  min-height: 50px;
  height: 50px;
  margin-bottom: 20%;
`;

/** Styles */

const isSearchableDimension = (value: string) => /(area|city|zone)/i.test(value);

export type DepthPathMap = {
  path: Array<number>;
  min: number;
  max: number;
};

export type PrepareSlicedDataOptions = {
  defaultPagination: number;
  depthPathMapping: DepthPathMap[];
  depthPath: number[];
};

const createFilterNotificationString = (msg: Object) => {
  return Object.entries(msg)
    .map(([key, value]) => `${key}=${value.join(", ")}`)
    .join(" and ");
};

const filterNotificationAction = (msg: Object) => {
  const finalFilterNotificationString = createFilterNotificationString(msg);
  setAlert(NotificationType.SUCCESS, "Applying your filter for " + finalFilterNotificationString); // Applying your filter for area=Delhi and city=Delhi and zone=Delhi
};

const AccordianChart: FC<AccordianChartProps> = ({
  chartDataProps: { payload, pending },
  selectedDimensions,
  dataUnit,
  isMetricDataLoading,
  onApplyFilter,
}) => {
  const [openTabPath, setOpenTabPath] = useState<number[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [renderableTreeData, setRenderableTreeData] = useState<AccordianNode[]>([]);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);

  const getFilteredDataBySearch = (
    dataInput: NestedTreeModel["data"] = [],
    text: string,
    dimensionLevel: keyof typeof DIMENSIONS
  ) => {
    const data = JSON.parse(JSON.stringify(dataInput));
    const dimensionValue = selectedDimensions.get(dimensionLevel) as string;

    const filteredData = isSearchableDimension(dimensionValue)
      ? data.filter(({ name }) => new RegExp(text, "i").test(name))
      : data;

    if (dimensionLevel === "THIRD_DIMENSION") {
      return filteredData;
    }

    return filteredData.map((datum: AccordianNode) => {
      datum.children = getFilteredDataBySearch(
        datum.children,
        text,
        dimensionLevel === "FIRST_DIMENSION" ? "SECOND_DIMENSION" : "THIRD_DIMENSION"
      );
      return datum;
    });
  };

  const payloadData = useMemo<NestedTreeModel["data"]>(() => safeGet(payload, "data", []), [payload]);

  /** It's a collation of both full data returned from model and the filtered data due to search */
  const sourceOfTruthData = useMemo(() => {
    const fullData: NestedTreeModel["data"] = payloadData;
    return searchText ? getFilteredDataBySearch(fullData, searchText, "FIRST_DIMENSION") : fullData;
  }, [payloadData, searchText]);

  const shouldShowSearchBox: boolean = useMemo(() => {
    let shouldShowSearch = false;
    if (!selectedDimensions) return false;
    const valuesIterator = selectedDimensions.values();
    for (const value of valuesIterator) {
      if (isSearchableDimension(value)) {
        shouldShowSearch = true;
        break;
      }
    }
    return shouldShowSearch;
  }, [selectedDimensions]);

  const openTab = useCallback(
    (shouldOpen = true, ...path: number[]) =>
      (e: React.MouseEvent) => {
        if (!shouldOpen) return;
        if (JSON.stringify(path) === JSON.stringify(openTabPath)) {
          setOpenTabPath(path.slice(0, -1));
        } else {
          setOpenTabPath(path);
        }
      },
    [openTabPath]
  );

  /**
   * To prepare pruned nested accordian data to show and handle pagination by honoring
   * MIN, MAX ranges at a given depth level or else default pagination is taken as max.
   */
  const preparedSlicedData = useCallback(
    (
      accordianNodes: AccordianNode[],
      { defaultPagination, depthPathMapping = [], depthPath = [] }: PrepareSlicedDataOptions
    ) => {
      const matchingDepthMapping = depthPathMapping.find(({ path }) => path.toString() === depthPath.toString());

      const shouldUseDefaultPagination = matchingDepthMapping ? false : true;

      const [MIN, MAX] = shouldUseDefaultPagination
        ? [0, defaultPagination]
        : [(matchingDepthMapping!.min = 0), matchingDepthMapping!.max];

      return (accordianNodes || []).slice(MIN, MAX).map(({ children, ...rest }, index) => ({
        ...rest,
        children: preparedSlicedData(children, {
          defaultPagination,
          depthPathMapping,
          depthPath: [...depthPath, index],
        }),
      }));
    },
    []
  );

  const setPaginatedRenderableData = useCallback(
    (depthPathMapping?: DepthPathMap[]) => {
      setRenderableTreeData(
        preparedSlicedData(sourceOfTruthData, {
          defaultPagination: PAGINATION_NUMBER,
          depthPathMapping,
        })
      );
    },
    [sourceOfTruthData]
  );

  useEffect(() => {
    setPaginatedRenderableData([]);
  }, [setPaginatedRenderableData]);

  const handleViewMore = useCallback(
    (depthPathMapping?: Array<DepthPathMap>) => () => {
      setPaginatedRenderableData(depthPathMapping);
    },
    [setPaginatedRenderableData]
  );

  const handleViewLess = useCallback(
    (depthPathMapping?: Array<DepthPathMap>) => () => {
      setPaginatedRenderableData(depthPathMapping);
    },
    [setPaginatedRenderableData]
  );

  const makeCountText = useCallback(
    (count) => {
      return dataUnit === DATA_UNIT.PERCENTAGE ? `${abbreviateNumber(count, 2)} %` : abbreviateNumber(count, 2);
    },
    [dataUnit]
  );

  const renderLoadMoreButtonSection = useCallback(
    ({
      shouldShowViewLess,
      shouldShowViewMore,
      onViewLess,
      onViewMore,
      isNested,
    }: {
      shouldShowViewLess: boolean;
      shouldShowViewMore: boolean;
      onViewLess: React.EventHandler<React.MouseEvent>;
      onViewMore: React.EventHandler<React.MouseEvent>;
      isNested?: boolean;
    }) => {
      return (
        <ButtonSection isNested={isNested}>
          {shouldShowViewLess && (
            <LoadButton type="default" onClick={onViewLess}>
              View Less
            </LoadButton>
          )}
          {shouldShowViewMore && (
            <LoadButton type="default" onClick={onViewMore}>
              View More
            </LoadButton>
          )}
        </ButtonSection>
      );
    },
    []
  );

  const renderThirdLevelAccordianItem = useCallback(
    (thirdLevelRow: AccordianNode, secondLevelRow: any, firstLevelRow: any) => {
      const thirdLevelfilterJson = {
        [thirdLevelRow.type]: [thirdLevelRow.name],
        [secondLevelRow.type]: [secondLevelRow.name],
        [firstLevelRow.type]: [firstLevelRow.name],
      };
      return (
        <ThirdLevelAccordianItem
          key={thirdLevelRow.name}
          onClick={() => (
            onApplyFilter(thirdLevelfilterJson),
            setIsFilterApplied(true),
            filterNotificationAction(thirdLevelfilterJson)
          )}
        >
          <FilterIcon />
          <p>{thirdLevelRow.name}</p>
          <p>{makeCountText(thirdLevelRow.count)}</p>
        </ThirdLevelAccordianItem>
      );
    },
    [makeCountText]
  );

  const renderSecondLevelAccordianItem = useCallback(
    (
      secondLevelRow: AccordianNode,
      secondLevelRowIndex: number,
      {
        firstLevelCollection,
        secondLevelCollection,
        firstLevelRowIndex,
      }: {
        firstLevelCollection: AccordianNode[];
        secondLevelCollection: AccordianNode[];
        firstLevelRowIndex: number;
      }
    ) => {
      const isOpen = openTabPath[0] === firstLevelRowIndex && openTabPath[1] === secondLevelRowIndex;
      const secondLevelfilterJson = {
        [secondLevelRow.type]: [secondLevelRow.name],
        [firstLevelCollection[firstLevelRowIndex].type]: [firstLevelCollection[firstLevelRowIndex].name],
      };
      return (
        <SecondLevelAccordianItem key={secondLevelRow.name} open={isOpen}>
          <SecondLevelHeader
            onClick={openTab(!!safeGet(secondLevelRow, "children", []).length, firstLevelRowIndex, secondLevelRowIndex)}
          >
            <HeaderItemName barPercentage={secondLevelRow.percentage}>
              <span
                onClick={() => (
                  onApplyFilter(secondLevelfilterJson),
                  setIsFilterApplied(true),
                  filterNotificationAction(secondLevelfilterJson)
                )}
                style={{ marginRight: "10px", fontSize: "18px" }}
              >
                <FilterIcon />
              </span>
              {secondLevelRow.name}
            </HeaderItemName>

            <span>{makeCountText(secondLevelRow.count)}</span>
            {/* apply 2 level filter */}
            {!!safeGet(secondLevelRow, "children", []).length &&
              (isOpen ? <UpOutlined className="text-center" /> : <DownOutlined className="text-center" />)}
          </SecondLevelHeader>
          {isOpen && (
            <SecondLevelExpandedBottom show={isOpen}>
              {safeGet(secondLevelRow, "children", []).map((thirdLevelRow: AccordianNode) =>
                renderThirdLevelAccordianItem(thirdLevelRow, secondLevelRow, firstLevelCollection[firstLevelRowIndex])
              )}
              {renderLoadMoreButtonSection({
                shouldShowViewLess:
                  safeGet(secondLevelRow, "children.length", 0) > 0 &&
                  safeGet(secondLevelRow, "children.length", 0) > PAGINATION_NUMBER,
                shouldShowViewMore:
                  safeGet(secondLevelRow, "children.length", 0) > 0 &&
                  safeGet(secondLevelRow, "children.length", 0) <
                    safeGet(
                      sourceOfTruthData,
                      `[${firstLevelRowIndex}].children[${secondLevelRowIndex}].children.length`,
                      0
                    ),
                onViewLess: handleViewLess([
                  {
                    path: [],
                    min: 0,
                    max: firstLevelCollection.length,
                  },
                  {
                    path: [firstLevelRowIndex],
                    min: 0,
                    max: secondLevelCollection.length,
                  },
                  {
                    path: [firstLevelRowIndex, secondLevelRowIndex],
                    min: 0,
                    max: Math.max(safeGet(secondLevelRow, "children.length", 0) - PAGINATION_NUMBER, PAGINATION_NUMBER),
                  },
                ]),
                onViewMore: handleViewMore([
                  {
                    path: [],
                    min: 0,
                    max: firstLevelCollection.length,
                  },
                  {
                    path: [firstLevelRowIndex],
                    min: 0,
                    max: secondLevelCollection.length,
                  },
                  {
                    path: [firstLevelRowIndex, secondLevelRowIndex],
                    min: 0,
                    max: safeGet(secondLevelRow, "children.length", 0) + PAGINATION_NUMBER,
                  },
                ]),
                isNested: true,
              })}
            </SecondLevelExpandedBottom>
          )}
        </SecondLevelAccordianItem>
      );
    },
    [openTab, makeCountText]
  );

  const renderFirstLevelAccordianItem = useCallback(
    (row: AccordianNode, firstLevelRowIndex: number, firstLevelCollection: AccordianNode[]) => {
      const isOpen = openTabPath[0] === firstLevelRowIndex;
      const firstLevelfilterJson = {
        [row.type]: [row.name],
      };
      return (
        <FirstLevelAccordianItem open={isOpen} key={row.name}>
          <FirstLevelHeader
            open={isOpen}
            barPercentage={row.percentage}
            onClick={openTab(!!safeGet(row, "children", []).length, firstLevelRowIndex)}
          >
            <FirstHeaderItemName>
              <span
                style={{ marginRight: "10px", fontSize: "21px" }}
                onClick={() => {
                  onApplyFilter(firstLevelfilterJson);
                  setIsFilterApplied(true);
                  filterNotificationAction(firstLevelfilterJson);
                }}
              >
                <FilterIcon />
              </span>
              {row.name}
            </FirstHeaderItemName>
            <span>{makeCountText(row.count)}</span>

            {!!safeGet(row, "children", []).length &&
              (isOpen ? <UpOutlined className="text-center" /> : <DownOutlined className="text-center" />)}
          </FirstLevelHeader>
          {/* {isOpen && safeGet(row, "children.length", 0) > 0 && ( */}
          {isOpen && (
            <FirstLevelExpandedBottom show={isOpen}>
              {safeGet(row, "children", []).map(
                (secondLevelRow: AccordianNode, secondLevelRowIndex: number, secondLevelCollection: AccordianNode[]) =>
                  renderSecondLevelAccordianItem(secondLevelRow, secondLevelRowIndex, {
                    firstLevelCollection,
                    secondLevelCollection,
                    firstLevelRowIndex,
                  })
              )}
              {renderLoadMoreButtonSection({
                shouldShowViewLess:
                  safeGet(row, "children.length", 0) > 0 && safeGet(row, "children.length", 0) > PAGINATION_NUMBER,
                shouldShowViewMore:
                  safeGet(row, "children.length", 0) > 0 &&
                  safeGet(row, "children.length", 0) <
                    safeGet(sourceOfTruthData, `[${firstLevelRowIndex}].children.length`, 0),
                onViewLess: handleViewLess([
                  {
                    path: [],
                    min: 0,
                    max: firstLevelCollection.length,
                  },
                  {
                    path: [firstLevelRowIndex],
                    min: 0,
                    max: Math.max(safeGet(row, "children.length", 0) - PAGINATION_NUMBER, PAGINATION_NUMBER),
                  },
                ]),
                onViewMore: handleViewMore([
                  {
                    path: [],
                    min: 0,
                    max: firstLevelCollection.length,
                  },
                  {
                    path: [firstLevelRowIndex],
                    min: 0,
                    max: safeGet(row, "children.length", 0) + PAGINATION_NUMBER,
                  },
                ]),
                isNested: true,
              })}
            </FirstLevelExpandedBottom>
          )}
        </FirstLevelAccordianItem>
      );
    },
    [openTab, makeCountText]
  );
  return (
    <Holder>
      {!pending && !isMetricDataLoading ? (
        <React.Fragment>
          {!isMetricDataLoading && payloadData.length === 0 && <NoDataText>No Data to Display</NoDataText>}
          {shouldShowSearchBox && payloadData && payloadData.length > 0 && (
            <SearchInputBox
              css={css`
                margin-top: 20px;
              `}
              onChange={setSearchText}
            />
          )}
          <br />
          {isFilterApplied && (
            <span
              onClick={() => {
                onApplyFilter({});
                setIsFilterApplied(false);
                setAlert(NotificationType.SUCCESS, "Clearing Filter");
              }}
            >
              <u>Clear Filter </u>
            </span>
          )}

          <AccordianSection>{(renderableTreeData || []).map(renderFirstLevelAccordianItem)}</AccordianSection>
          {renderLoadMoreButtonSection({
            shouldShowViewLess: renderableTreeData.length > 0 && renderableTreeData.length > PAGINATION_NUMBER,
            shouldShowViewMore: renderableTreeData.length > 0 && renderableTreeData.length < sourceOfTruthData.length,
            onViewLess: handleViewLess([
              {
                path: [],
                min: 0,
                max: Math.max(renderableTreeData.length - PAGINATION_NUMBER, PAGINATION_NUMBER),
              },
            ]),
            onViewMore: handleViewMore([
              {
                path: [],
                min: 0,
                max: renderableTreeData.length + PAGINATION_NUMBER,
              },
            ]),
          })}
        </React.Fragment>
      ) : (
        <Shimmer css={shimmerCSS} barsConfig={shimmerConfig} />
      )}
    </Holder>
  );
};

export default memo(AccordianChart);
